import React, { useMemo } from "react";
import { StaffVisualizer, type StaffFilterMode } from "../StaffVisualizer";
import { GrandStaffVisualizer } from "../piano/GrandStaffVisualizer";
import type { NoteName } from "../../lib/guitarEngine";
import { getNoteAtPosition } from "../../lib/guitarEngine";
import { useInstrument } from "../../contexts/InstrumentContext";
import { buildScaleGrandStaffEvents } from "../../lib/scaleStaffEvents";
import type { PracticeNoteEvent } from "../../lib/practiceNoteModel";

export interface PracticeStaffProps {
  rootNote: NoteName;
  scaleType: string;
  activeNote?: { string: number; fret: number } | null;
  filterMode?: StaffFilterMode;
  highlightNote?: NoteName | null;
  playbackSequence?: Array<{ note: NoteName; string: number; fret: number }>;
  currentSeqIndex?: number;
  learnSurface?: boolean;
  /** Widget compacto (Explore): sin título ni leyenda, altura reducida */
  compact?: boolean;
  /** Pentagrama atmosférico (marca de agua) en Explore */
  atmospheric?: boolean;
  /** Escala fija: no añade notas al tocar en el mástil */
  staticScaleOnly?: boolean;
  selectedDegree?: string | null;
  onNoteSelect?: (event: PracticeNoteEvent) => void;
  /** Si se define (piano), el pentagrama muestra solo estas notas en lugar de la escala completa. */
  trebleNotesOverride?: PracticeNoteEvent[];
}

export const PracticeStaff: React.FC<PracticeStaffProps> = (props) => {
  const { surface } = useInstrument();
  const { rootNote, scaleType, activeNote, selectedDegree, onNoteSelect, trebleNotesOverride } =
    props;

  const grandStaffEvents = useMemo(() => {
    if (trebleNotesOverride) {
      return { treble: trebleNotesOverride, bass: [] as PracticeNoteEvent[] };
    }
    return buildScaleGrandStaffEvents(rootNote, scaleType);
  }, [trebleNotesOverride, rootNote, scaleType]);

  const activeMidi = useMemo(() => {
    if (!activeNote || surface !== "keyboard") return null;
    return getNoteAtPosition(activeNote.string, activeNote.fret).midi;
  }, [activeNote, surface]);

  if (surface === "keyboard") {
    return (
      <GrandStaffVisualizer
        trebleNotes={grandStaffEvents.treble}
        bassNotes={grandStaffEvents.bass}
        activeMidi={activeMidi}
        selectedDegree={selectedDegree}
        onNoteSelect={onNoteSelect}
        className="w-full"
      />
    );
  }

  return <StaffVisualizer {...props} />;
};
